import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { usePortraitOrientation } from '@shared/hooks/useOrientation'

// 初始顯示縮放倍率：在「寬度剛好貼滿」的基礎上再放大／縮小一些，橫直式視覺比例不同故分開設定
const INITIAL_SCALE_LANDSCAPE = 0.65
const INITIAL_SCALE_PORTRAIT = 1.25
const MAX_SCALE = 4

function getInitialScale(isPortrait) {
  return isPortrait ? INITIAL_SCALE_PORTRAIT : INITIAL_SCALE_LANDSCAPE
}

// 地圖原圖尺寸（同 marketMapBooths.js 的座標基準），用於計算全螢幕下「完整顯示、不裁切」的實際 px 寬高
const MAP_IMAGE_WIDTH = 1101
const MAP_IMAGE_HEIGHT = 792

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function distanceBetween(touches) {
  const [a, b] = touches
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function midpoint(touches) {
  const [a, b] = touches
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 }
}

/**
 * 市集地圖全螢幕層（第二層）：地圖圖片＋熱區可縮放/拖曳檢視，
 * 點擊個別熱區才會觸發第三層的攤位詳情 dialog（由父層 onBoothClick 決定）。
 * 直向裝置時整體旋轉 90 度變成直式構圖，熱區色塊上的文字反向旋轉回來維持可讀。
 */
export default function MarketMapFullscreen({ mapSrc, mapAlt, booths, labels = [], onClose, onBoothClick }) {
  const isPortrait = usePortraitOrientation()
  const viewportRef = useRef(null)
  const [transform, setTransform] = useState(() => ({ scale: getInitialScale(isPortrait), x: 0, y: 0 }))
  const gestureRef = useRef(null)
  // 地圖在「完整顯示、不裁切」時的實際 px 尺寸，由 viewport 實際量測寬高換算而來（避免 CSS 百分比／aspect-ratio 連鎖失效）
  const [fitSize, setFitSize] = useState(null)

  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const measure = () => {
      const { width } = el.getBoundingClientRect()
      if (!width) return
      // 初始顯示改為寬度直接貼滿 viewport（不再用 contain 縮到最小軸），
      // 高度依圖片比例算出、可能超出 viewport，交由使用者縮放/拖曳查看其餘部分
      const imageRatio = MAP_IMAGE_WIDTH / MAP_IMAGE_HEIGHT
      setFitSize({ width, height: width / imageRatio })
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [isPortrait])

  // 切換直橫向時重置縮放/位移，避免殘留上一個方向算出來的座標
  useEffect(() => {
    setTransform({ scale: getInitialScale(isPortrait), x: 0, y: 0 })
  }, [isPortrait])

  const clampPan = useCallback((scale, x, y) => {
    const viewport = viewportRef.current
    if (!viewport || !fitSize) return { x, y }
    const { width: viewportW, height: viewportH } = viewport.getBoundingClientRect()
    // 地圖初始高度可能已經比 viewport 高（寬度貼滿、高度依比例算出），
    // 因此 pan 邊界要用「實際內容尺寸 vs viewport 尺寸」的差值計算，而非只看 scale 是否大於 1
    const contentW = fitSize.width * scale
    const contentH = fitSize.height * scale
    const maxX = Math.max(0, (contentW - viewportW) / 2)
    const maxY = Math.max(0, (contentH - viewportH) / 2)
    return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) }
  }, [fitSize])

  // React 合成事件的 wheel/touchmove 監聽在 root 上預設是 passive，
  // e.preventDefault() 會被瀏覽器忽略並丟出警告，因此改用原生 addEventListener 手動指定 passive: false
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const onWheel = (e) => {
      e.preventDefault()
      setTransform((prev) => {
        const nextScale = clamp(prev.scale - e.deltaY * 0.01, getInitialScale(isPortrait), MAX_SCALE)
        const { x, y } = clampPan(nextScale, prev.x, prev.y)
        return { scale: nextScale, x, y }
      })
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [clampPan, isPortrait])

  // touchmove 也需要 preventDefault 才能阻止瀏覽器原生的頁面縮放/捲動，理由同上改用原生監聽
  useEffect(() => {
    const el = viewportRef.current
    if (!el) return

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        gestureRef.current = {
          mode: 'pinch',
          startDistance: distanceBetween(e.touches),
          startScale: transform.scale,
          startMid: midpoint(e.touches),
          startX: transform.x,
          startY: transform.y,
        }
      } else if (e.touches.length === 1) {
        gestureRef.current = {
          mode: 'pan',
          startPoint: { x: e.touches[0].clientX, y: e.touches[0].clientY },
          startX: transform.x,
          startY: transform.y,
          moved: false,
        }
      }
    }

    const onTouchMove = (e) => {
      const gesture = gestureRef.current
      if (!gesture) return
      e.preventDefault()

      if (gesture.mode === 'pinch' && e.touches.length === 2) {
        const newDistance = distanceBetween(e.touches)
        const nextScale = clamp(gesture.startScale * (newDistance / gesture.startDistance), getInitialScale(isPortrait), MAX_SCALE)
        setTransform(() => {
          const { x, y } = clampPan(nextScale, gesture.startX, gesture.startY)
          return { scale: nextScale, x, y }
        })
      } else if (gesture.mode === 'pan' && e.touches.length === 1) {
        const dx = e.touches[0].clientX - gesture.startPoint.x
        const dy = e.touches[0].clientY - gesture.startPoint.y
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) gesture.moved = true
        setTransform((prev) => {
          // 直向旋轉 90 度後，畫面上的左右拖曳對應到旋轉前座標系的上下移動，需互換並反向對應一軸
          const [rawDx, rawDy] = isPortrait ? [dy, -dx] : [dx, dy]
          const { x, y } = clampPan(prev.scale, gesture.startX + rawDx, gesture.startY + rawDy)
          return { ...prev, x, y }
        })
      }
    }

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) gestureRef.current = null
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [clampPan, isPortrait, transform])

  const handleBoothTap = (booth, e) => {
    if (gestureRef.current?.moved) return
    onBoothClick(booth, e)
  }

  const resetView = () => setTransform({ scale: getInitialScale(isPortrait), x: 0, y: 0 })

  const mapAreaStyle = useMemo(() => ({
    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
    transition: gestureRef.current ? 'none' : 'transform 150ms ease-out',
  }), [transform])

  return (
    <div className="fixed inset-0 z-[9999]" style={{ background: '#171717' }}>
      <div
        ref={viewportRef}
        className="absolute overflow-hidden touch-none"
        style={
          isPortrait
            ? {
              top: 0,
              left: '100%',
              height: '100dvw',
              width: '100dvh',
              transform: 'rotate(90deg)',
              transformOrigin: 'top left',
            }
            : { top: 0, left: 0, right: 0, bottom: 0, height: '100dvh', width: '100dvw' }
        }
        onDoubleClick={resetView}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={mapAreaStyle}
        >
          {fitSize && (
            <div
              className="relative"
              style={{
                containerType: 'inline-size',
                width: fitSize.width,
                height: fitSize.height,
                background: 'white',
                boxShadow: isPortrait ? '0 16px 0 white, 0 -24px 0 white' : 'none',
              }}
            >
              <img src={mapSrc} alt={mapAlt} className="block w-full h-full" draggable={false} />
              {booths.map((booth) => (
                <button
                  key={booth.code}
                  type="button"
                  onClick={(e) => handleBoothTap(booth, e)}
                  className="absolute cursor-pointer flex items-center justify-center text-center"
                  style={{ ...booth.style, background: booth.color, borderRadius: 'clamp(2px, 0.4cqw, 4px)', padding: 'clamp(1px, 0.4cqw, 4px)' }}
                  aria-label={booth.shortTitle}
                >
                  <span
                    className="line-clamp-4"
                    style={{
                      color: 'white',
                      fontFamily: 'Noto Sans TC',
                      fontSize: 'clamp(4px, 1.2cqw, 18px)',
                      fontWeight: 700,
                      lineHeight: 1.25,
                      whiteSpace: 'pre-line',
                      display: 'inline-block',
                      // 地圖整體旋轉 90 度後，色塊上的文字反向旋轉回來，維持使用者直向可讀
                      transform: isPortrait ? 'rotate(-90deg)' : undefined,
                    }}
                  >
                    {booth.code}{'\n'}{booth.mapLabel}
                  </span>
                </button>
              ))}
              {labels.map((label) => (
                <span
                  key={label.text}
                  className="absolute pointer-events-none flex flex-col items-center justify-center"
                  style={{
                    ...label.style,
                    transform: `translate(-50%, -50%)${isPortrait ? ' rotate(-90deg)' : ''}`,
                    color: label.boxed ? 'white' : '#000',
                    fontFamily: 'Noto Sans TC',
                    fontSize: 'clamp(8px, 1.2cqw, 16px)',
                    fontWeight: 700,
                    lineHeight: label.boxed ? 1.3 : undefined,
                    ...(label.boxed
                      ? {
                        background: '#595758',
                        border: '2px solid #171717',
                        borderRadius: 'clamp(3px, 0.4cqw, 6px)',
                        padding: 'clamp(1px, 0.7cqw, 8px) clamp(0px, 0.8cqw, 10px)',
                      }
                      : {
                        borderBottom: '2px solid #000',
                        paddingBottom: 2,
                      }),
                  }}
                >
                  {label.text.split('\n').map((line, i) => (
                    <span key={i} className="whitespace-nowrap">{line}</span>
                  ))}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        onClick={onClose}
        aria-label="close"
        className="fixed z-[10000] w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
        style={{ top: 'max(1rem, env(safe-area-inset-top))', right: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <X size={20} className="text-[#3D3A36]" />
      </button>
    </div>
  )
}
